import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConversationBubble from './ConversationBubble';
import VoiceConfirmCard, { ConfirmCardData } from './VoiceConfirmCard';
import VoiceQuickPicks, { VoiceQuickPickOption } from './VoiceQuickPicks';
import HistoryModal from './HistoryModal';
import Icon from './ui/Icon';
import type { VoiceMessage, VoiceSession } from '../utils/voiceSessionStorage';
import type { AssistantSuggestedQueryPill } from '../constants/assistantSuggestedQueries';
import type { AssistantPageContext } from '../hooks/useDashboardAssistantContext';
import VoiceAssistantRailEmptyState from './VoiceAssistantRailEmptyState';
import './GlobalVoiceAssistantUI.css';
import './gva-rail.css';

const RAIL_PANEL_ID = 'gva-rail-panel';

export interface VoiceAssistantRailProps {
  messages: VoiceMessage[];
  isProcessing: boolean;
  isRecording: boolean;
  showHistoryModal: boolean;
  pastSessions: VoiceSession[];
  selectedPastSession?: VoiceSession | null;
  currentTranscript?: string;
  confirmCard?: ConfirmCardData | null;
  quickPicks?: VoiceQuickPickOption[];
  dynamicSuggestedQueries?: string[];
  pageSuggestedQueries?: AssistantSuggestedQueryPill[];
  assistantContext?: AssistantPageContext;
  onQuickPickSelect?: (option: VoiceQuickPickOption) => void;
  onCollapse: () => void;
  onStartVoiceInput: () => void;
  onStopVoiceInput?: () => void;
  onTranscription: (text: string) => void;
  onProcessingChange: (isProcessing: boolean) => void;
  onOpenHistory?: (show?: boolean) => void;
  onCloseHistoryModal?: () => void;
  onSelectPastSession?: (session: VoiceSession) => void;
  onLoadSession?: (session: VoiceSession) => void;
  onLoadSelectedSession?: () => void;
  onStartNewChat?: () => void;
  onClearHistory?: () => void;
  notify?: (message: string, type?: string) => void;
}

const VoiceAssistantRail: React.FC<VoiceAssistantRailProps> = ({
  messages,
  isProcessing,
  isRecording,
  showHistoryModal,
  pastSessions,
  selectedPastSession,
  currentTranscript,
  confirmCard,
  quickPicks = [],
  dynamicSuggestedQueries = [],
  pageSuggestedQueries = [],
  assistantContext,
  onQuickPickSelect,
  onCollapse,
  onStartVoiceInput,
  onStopVoiceInput,
  onTranscription,
  onProcessingChange,
  onOpenHistory,
  onCloseHistoryModal,
  onSelectPastSession,
  onLoadSession,
  onLoadSelectedSession,
  onStartNewChat,
  onClearHistory,
  notify,
}) => {
  const { t } = useTranslation();
  const threadRef = useRef<HTMLDivElement>(null);
  const [textInput, setTextInput] = useState('');
  const [waveLevels, setWaveLevels] = useState<number[]>([]);

  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, currentTranscript, isProcessing, confirmCard]);

  useEffect(() => {
    const handleWaveform = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setWaveLevels(Array.isArray(detail?.levels) ? detail.levels : []);
    };
    window.addEventListener('voiceWaveform', handleWaveform as EventListener);
    return () => window.removeEventListener('voiceWaveform', handleWaveform as EventListener);
  }, []);

  const handleHistoryClose = () => {
    onCloseHistoryModal?.() ?? onOpenHistory?.(false);
  };

  const handleSuggestedQuery = (query: string) => {
    if (isProcessing || isRecording) return;
    onTranscription(query);
  };

  const handleTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = textInput.trim();
    if (!trimmed || isProcessing) return;
    setTextInput('');
    onTranscription(trimmed);
  };

  const suggestionPills = pageSuggestedQueries;

  return (
    <aside className="gva-rail" id={RAIL_PANEL_ID} aria-label={t('voice.aiAssistant', 'AI Assistant')}>
      <header className="gva-rail-header">
        <div className="gva-rail-header-brand">
          <h2 className="gva-rail-title">{t('voice.aiAssistant', 'AI Assistant')}</h2>
        </div>
        <div className="gva-rail-header-actions">
          <button
            type="button"
            className="gva-rail-icon-btn"
            onClick={onCollapse}
            title={t('dashboard.assistant.collapse', 'Collapse assistant')}
            aria-label={t('dashboard.assistant.collapse', 'Collapse assistant')}
          >
            <Icon name="minimize" size={18} />
          </button>
          <button
            type="button"
            className="gva-rail-icon-btn"
            onClick={() => onOpenHistory?.(true)}
            title={t('voice.modal.transactionHistory')}
            aria-label={t('voice.modal.transactionHistory')}
          >
            <Icon name="chat" size={18} />
          </button>
          <button
            type="button"
            className="gva-rail-icon-btn"
            onClick={onStartNewChat}
            title={t('voice.newChat', 'New chat')}
            aria-label={t('voice.newChat', 'New chat')}
          >
            <Icon name="add" size={18} />
          </button>
        </div>
      </header>

      <div className="gva-rail-body">
        <div className="gva-rail-thread" ref={threadRef} aria-label={t('voice.chatArea', 'Chat')}>
          {messages.length === 0 && !isRecording && !isProcessing && (
            <VoiceAssistantRailEmptyState
              pageSuggestedQueries={suggestionPills}
              assistantContext={assistantContext}
              disabled={isProcessing || isRecording}
              onSuggestedQuery={handleSuggestedQuery}
            />
          )}

          {messages.map((msg) => (
            <ConversationBubble key={msg.id} message={msg} dark={false} />
          ))}

          {isRecording && (
            <div className="gva-bubble-row gva-bubble-row-user gva-rail-live-row">
              <div className="gva-bubble-col gva-bubble-col-user">
                <p className="gva-bubble-label">{t('voice.you', 'You')}</p>
                <div className="gva-bubble gva-bubble-user gva-bubble-live">
                  <p>{currentTranscript?.trim() || t('voice.listening', 'Listening…')}</p>
                </div>
                <span className="gva-bubble-time gva-bubble-time-live">
                  {t('voice.listening', 'Listening…')}
                </span>
              </div>
            </div>
          )}

          {isRecording && (
            <div className="gva-rail-waveform" aria-hidden="true">
              {(waveLevels.length ? waveLevels : new Array(20).fill(0)).map((level, i) => (
                <span
                  key={i}
                  className="gva-rail-waveform-bar"
                  style={{ height: `${Math.max(8, Math.min(100, level * 100))}%` }}
                />
              ))}
            </div>
          )}

          {isProcessing && (
            <div className="gva-rail-processing" aria-label={t('voice.processing', 'Processing…')}>
              <span className="gva-rail-processing-dot" />
              <span className="gva-rail-processing-dot" />
              <span className="gva-rail-processing-dot" />
            </div>
          )}

          {confirmCard && <VoiceConfirmCard data={confirmCard} variant="inline" />}

          {quickPicks.length > 0 && onQuickPickSelect && (
            <VoiceQuickPicks
              options={quickPicks}
              onSelect={onQuickPickSelect}
              disabled={isProcessing}
            />
          )}
        </div>

        <form className="gva-rail-text-bar" onSubmit={handleTextSubmit}>
          <input
            className="gva-rail-text-input"
            type="text"
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder={t('voice.typePlaceholder', 'Type your message…')}
            disabled={isProcessing || isRecording}
            aria-label={t('voice.typePlaceholder', 'Type your message…')}
          />
          <button
            type="submit"
            className="gva-rail-text-send"
            disabled={isProcessing || isRecording || !textInput.trim()}
          >
            {t('voice.send', 'Send')}
          </button>
        </form>
      </div>

      <footer className="gva-rail-footer">
        <div className="gva-rail-controls">
          <button
            type="button"
            className={`gva-rail-mic-btn${isRecording ? ' recording' : ''}`}
            onClick={() => {
              if (isRecording) {
                onStopVoiceInput?.();
              } else if (!isProcessing) {
                onStartVoiceInput();
              }
            }}
            disabled={isProcessing && !isRecording}
            aria-pressed={isRecording}
            aria-label={
              isRecording
                ? t('voice.stopRecording', 'Stop recording')
                : t('dashboard.assistant.startSpeaking')
            }
          >
            <Icon name={isRecording ? 'mic-stop' : 'mic'} size={22} />
            {isRecording
              ? t('voice.stopRecording', 'Stop Speaking')
              : t('dashboard.assistant.startSpeaking')}
          </button>
        </div>

      </footer>

      {showHistoryModal && (
        <HistoryModal
          isOpen={showHistoryModal}
          onClose={handleHistoryClose}
          pastSessions={pastSessions}
          selectedSession={selectedPastSession}
          onSelectSession={onSelectPastSession || (() => {})}
          onLoadSession={onLoadSession || (() => {})}
          onLoadSelectedSession={onLoadSelectedSession || (() => {})}
          onStartNewChat={onStartNewChat || (() => {})}
          onClearHistory={onClearHistory || (() => {})}
          messages={messages}
          notify={notify}
        />
      )}
    </aside>
  );
};

export default VoiceAssistantRail;
